import { useLayoutEffect, useMemo, useRef, useState } from "react";
import type { BillingDailyCredits } from "@/lib/api";
import {
	buildUsageChartData,
	formatCredits,
	type UsageChartBucket,
	type UsageChartCategory,
	type UsageChartData,
	type UsageChartTab,
} from "@/lib/billing";
import { BarTrack, MIN_BAR_SEGMENT_WIDTH } from "@/components/billing/BarTrack";
import { ROW_HOVER_BG } from "@/lib/row-styles";
import { sum } from "@/lib/sum";

/** プロット領域の高さ (px)。カードの残りは見出し・軸ラベルが使う。 */
const PLOT_HEIGHT = 180;

/** 積み上げ縦棒の最大の太さ (px)。スロットが広くても棒だけが太らないよう上限を切る。 */
const MAX_COLUMN_WIDTH = 24;

/** 積み上げの隣接セグメントを分ける地色の隙間 (px)。 */
const SEGMENT_GAP = 2;

/** 縦棒の最上段の最小の高さ (px)。消費が僅少な日も棒が消えないようにする。 */
const MIN_TOP_SEGMENT_HEIGHT = 0.75;

/**
 * 強調したカテゴリのセグメントの最小の高さ (px)。僅少でも見えるよう最上段より太くする。
 * 広げた分は積み重なって棒が合計より高くなるので、ふだんの積み上げには効かせない。
 */
const MIN_FOCUSED_SEGMENT_HEIGHT = 2;

/** 軸・目盛ラベルの余白。左は目盛の数字の幅に応じて実行時に決める。 */
const MARGIN = { top: 8, right: 8, bottom: 22 };

/** 目盛用の短い数値表記 (例: 12万)。正確な値はツールチップが担う。 */
const compactFormat = new Intl.NumberFormat("ja-JP", {
	notation: "compact",
	maximumFractionDigits: 1,
});

/** ポインタが乗っている一覧の行。タブの見出しなら、そのタブのカテゴリをまとめて強調する。 */
interface FocusTarget {
	kind: "tab" | "category";
	id: string;
}

/**
 * 使用量グラフのカード。期間内のクレジット消費をカテゴリごとの色で積み上げて時系列に描き、
 * 横に料金表のタブごとにまとめたカテゴリ別の消費を横棒で並べる (横棒の色が凡例を兼ねる)。
 * 期間の消費が多いタブほど下に積み、タブの中も消費が多いカテゴリほど下に積む。
 * 期間が 1 ヶ月以下なら日別の縦棒、超えるなら
 * 面グラフになる (形の判定は buildUsageChartData)。一覧の行にポインタを乗せると、
 * そのカテゴリ (タブの見出しならタブのカテゴリ) だけが色を保ち、残りは灰色の 1 段にまとまる。
 * プロットへのホバーでは日付とカテゴリ別の内訳をツールチップ表示する。
 */
export function BillingUsageChart({
	rows,
	from,
	now,
}: {
	rows: readonly BillingDailyCredits[];
	from: Date | null;
	now: Date;
}) {
	const data = useMemo(() => buildUsageChartData(rows, from, now), [rows, from, now]);
	const [hovered, setHovered] = useState<FocusTarget | null>(null);

	const showCategories = data.categories.length >= 2;
	// 再取得でカテゴリが消えたり一覧が隠れたりすると pointerleave が届かないので、
	// 一覧に行がある間だけ強調を効かせ、効かなくなったらホバーも忘れる
	// (覚えたままだと、後でカテゴリが戻ったときにポインタが無いのに強調が蘇る)。
	const focusIds = showCategories ? focusedCategoryIds(data, hovered) : null;
	if (focusIds === null && hovered !== null) setHovered(null);
	const focus = focusIds === null ? null : hovered;

	return (
		<section
			data-testid="billing-usage-chart"
			data-chart-kind={data.kind}
			style={{
				flex: "1 1 360px",
				minWidth: 0,
				display: "flex",
				flexDirection: "column",
				gap: 8,
				padding: 16,
				background: "var(--surface-1)",
				boxShadow: "var(--shadow-ring)",
				borderRadius: "var(--radius-4)",
			}}
		>
			<span
				style={{
					fontSize: "var(--text-ui)",
					fontWeight: 600,
					color: "var(--fg-2)",
					letterSpacing: "var(--tracking-heading)",
				}}
			>
				クレジット消費の推移
			</span>
			{data.categories.length === 0 ? (
				<div
					style={{
						flex: 1,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						minHeight: PLOT_HEIGHT,
						color: "var(--fg-3)",
						fontSize: "var(--text-ui)",
					}}
				>
					対象期間のクレジット消費はありません
				</div>
			) : (
				// カテゴリ別の一覧はプロットの右に置き、幅が足りなければ下へ折り返す。
				<div
					style={{ display: "flex", flexWrap: "wrap", gap: "8px 24px", alignItems: "flex-start" }}
				>
					<div style={{ flex: "1 1 420px", minWidth: 0 }}>
						<UsagePlot data={data} focusIds={focusIds} />
					</div>
					{showCategories && <CategoryList tabs={data.tabs} focus={focus} onHover={setHovered} />}
				</div>
			)}
		</section>
	);
}

/** 一覧の行が指す強調対象のカテゴリ。行が今のデータに無ければ null。 */
function focusedCategoryIds(data: UsageChartData, target: FocusTarget | null): string[] | null {
	if (target === null) return null;
	if (target.kind === "tab") {
		return (
			data.tabs.find((t) => t.tab.id === target.id)?.categories.map((c) => c.category.id) ?? null
		);
	}
	return data.categories.some((c) => c.category.id === target.id) ? [target.id] : null;
}

/**
 * カテゴリ別の消費。料金表のタブの見出しの下に、カテゴリを名前と横棒の長さで並べる
 * (横棒は最大のカテゴリを全幅とし、色はグラフの系列色)。行にポインタが乗っている間は
 * グラフでそのカテゴリ (見出しならタブのカテゴリ) を強調する。
 */
function CategoryList({
	tabs,
	focus,
	onHover,
}: {
	tabs: readonly UsageChartTab[];
	focus: FocusTarget | null;
	onHover: (target: FocusTarget | null) => void;
}) {
	const maxCredits = Math.max(...tabs.flatMap((t) => t.categories.map((c) => c.credits)));
	const isFocused = (kind: FocusTarget["kind"], id: string) =>
		focus !== null && focus.kind === kind && focus.id === id;
	return (
		<div
			data-testid="billing-usage-chart-categories"
			// 行の間の隙間で強調が途切れないよう、外れたことは一覧全体で判定する。
			onPointerLeave={() => onHover(null)}
			style={{ flex: "0 1 280px", minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}
		>
			<span style={{ padding: "0 6px 4px", fontSize: "var(--text-caption)", color: "var(--fg-3)" }}>
				カテゴリ別
			</span>
			{tabs.map(({ tab, credits, categories }, tabIndex) => (
				<div
					key={tab.id}
					style={{
						display: "flex",
						flexDirection: "column",
						gap: 2,
						marginTop: tabIndex > 0 ? 6 : 0,
					}}
				>
					<ListRow
						testId={`billing-usage-chart-tab-${tab.id}`}
						focused={isFocused("tab", tab.id)}
						onEnter={() => onHover({ kind: "tab", id: tab.id })}
						label={tab.label}
						credits={credits}
						strong
						swatches={categories.map((c) => c.category.color)}
					/>
					{categories.map(({ category, credits: categoryCredits }) => (
						<ListRow
							key={category.id}
							testId={`billing-usage-chart-category-${category.id}`}
							focused={isFocused("category", category.id) || isFocused("tab", tab.id)}
							onEnter={() => onHover({ kind: "category", id: category.id })}
							label={category.label}
							credits={categoryCredits}
							indent
							bar={{ ratio: categoryCredits / maxCredits, color: category.color }}
						/>
					))}
				</div>
			))}
		</div>
	);
}

/** 一覧の 1 行。タブの見出しは系列色の見本を、カテゴリは横棒を持つ。 */
function ListRow({
	testId,
	focused,
	onEnter,
	label,
	credits,
	strong = false,
	indent = false,
	swatches,
	bar,
}: {
	testId: string;
	focused: boolean;
	onEnter: () => void;
	label: string;
	credits: number;
	strong?: boolean;
	indent?: boolean;
	swatches?: readonly string[];
	bar?: { ratio: number; color: string };
}) {
	return (
		<div
			data-testid={testId}
			onPointerEnter={onEnter}
			style={{
				display: "flex",
				flexDirection: "column",
				gap: 4,
				padding: indent ? "4px 6px 4px 18px" : "4px 6px",
				borderRadius: "var(--radius-2)",
				background: focused ? ROW_HOVER_BG : undefined,
			}}
		>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					gap: 8,
					fontSize: "var(--text-caption)",
				}}
			>
				{swatches !== undefined && (
					<span style={{ display: "flex", gap: 2, flexShrink: 0 }}>
						{swatches.map((color) => (
							<span
								key={color}
								style={{ width: 6, height: 10, borderRadius: "var(--radius-1)", background: color }}
							/>
						))}
					</span>
				)}
				<span
					title={label}
					data-tooltip-if-truncated
					style={{
						minWidth: 0,
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
						fontWeight: strong ? 500 : undefined,
						color: focused ? "var(--fg-0)" : strong ? "var(--fg-1)" : "var(--fg-2)",
					}}
				>
					{label}
				</span>
				<span
					style={{
						flexShrink: 0,
						marginLeft: "auto",
						color: strong ? "var(--fg-1)" : "var(--fg-2)",
						fontVariantNumeric: "tabular-nums",
					}}
				>
					{formatCredits(credits)}
				</span>
			</div>
			{bar !== undefined && (
				<BarTrack>
					<div
						style={{
							width: `${bar.ratio * 100}%`,
							minWidth: MIN_BAR_SEGMENT_WIDTH,
							height: "100%",
							borderRadius: "var(--radius-full)",
							background: bar.color,
						}}
					/>
				</BarTrack>
			)}
		</div>
	);
}

/** 積み上げる帯 1 段分。 */
interface Band {
	key: string;
	fill: string;
	credits: (bucket: UsageChartBucket) => number;
	focused: boolean;
}

/**
 * グラフの帯を下から順に返す。ふだんはカテゴリごとに系列色で 1 段ずつ。強調している間は、
 * 強調したカテゴリを系列色のまま下段に寄せ、残りを灰色の 1 段にまとめる。
 */
function chartBands(
	categories: readonly UsageChartCategory[],
	focusIds: readonly string[] | null,
): Band[] {
	const band = ({ category }: UsageChartCategory, focused: boolean): Band => ({
		key: category.id,
		fill: category.color,
		credits: (b) => b.byCategory[category.id] ?? 0,
		focused,
	});
	if (focusIds === null) return categories.map((c) => band(c, false));
	const focused = categories.filter((c) => focusIds.includes(c.category.id));
	const rest = categories.filter((c) => !focusIds.includes(c.category.id));
	return [
		...focused.map((c) => band(c, true)),
		{
			key: "rest",
			fill: "var(--chart-rest)",
			// 合計からの引き算は浮動小数の端数で 0 にならず、細い帯が残ることがあるので足し上げる。
			credits: (b) => sum(rest, (c) => b.byCategory[c.category.id] ?? 0),
			focused: false,
		},
	];
}

/** ホバー中のバケットとポインタ位置 (プロット左上基準の px)。 */
interface HoverState {
	index: number;
	pointerX: number;
}

/** 描画本体。コンテナ幅を測って SVG を組み立て、ホバー層とツールチップを重ねる。 */
function UsagePlot({
	data,
	focusIds,
}: {
	data: UsageChartData;
	focusIds: readonly string[] | null;
}) {
	const containerRef = useRef<HTMLDivElement>(null);
	const [width, setWidth] = useState(0);
	const [hover, setHover] = useState<HoverState | null>(null);

	useLayoutEffect(() => {
		const el = containerRef.current;
		if (!el) return;
		const observer = new ResizeObserver((entries) => setWidth(entries[0].contentRect.width));
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	const yMax = niceMax(Math.max(...data.buckets.map((b) => b.total)));
	const yTicks = buildYTicks(yMax);
	// 左余白は目盛ラベルの最長文字列に合わせる (およそ 1 文字 7px + 目盛との間 10px)。
	const marginLeft = Math.max(...yTicks.map((t) => compactFormat.format(t).length)) * 7 + 10;

	const plotWidth = Math.max(width - marginLeft - MARGIN.right, 0);
	const height = MARGIN.top + PLOT_HEIGHT + MARGIN.bottom;
	const buckets = data.buckets;

	// 縦棒はスロット中央、面グラフは端から端まで使う点配置。
	const xForIndex = (index: number): number => {
		if (data.kind === "column") return ((index + 0.5) / buckets.length) * plotWidth;
		if (buckets.length === 1) return plotWidth / 2;
		return (index / (buckets.length - 1)) * plotWidth;
	};
	const yForValue = (value: number): number =>
		PLOT_HEIGHT - (yMax > 0 ? (value / yMax) * PLOT_HEIGHT : 0);

	const indexForPointerX = (px: number): number => {
		if (buckets.length <= 1) return 0;
		if (data.kind === "column") {
			return Math.min(
				Math.max(Math.floor((px / plotWidth) * buckets.length), 0),
				buckets.length - 1,
			);
		}
		return Math.min(
			Math.max(Math.round((px / plotWidth) * (buckets.length - 1)), 0),
			buckets.length - 1,
		);
	};

	const xTicks = buildXTicks(buckets, plotWidth);
	const bands = chartBands(data.categories, focusIds);

	// 再取得でバケット数が縮むと (ポインタが乗ったまま組織を切り替えた場合など)
	// ホバー中の index が範囲外になりうるため、実体が引けたときだけホバー描画する。
	const hoveredBucket = hover !== null ? buckets.at(hover.index) : undefined;

	return (
		<div ref={containerRef} style={{ position: "relative", width: "100%" }}>
			{width > 0 && (
				<svg
					width={width}
					height={height}
					style={{ display: "block" }}
					onPointerMove={(e) => {
						const rect = e.currentTarget.getBoundingClientRect();
						const px = e.clientX - rect.left - marginLeft;
						if (px < -12 || px > plotWidth + 12) {
							setHover(null);
							return;
						}
						setHover({ index: indexForPointerX(px), pointerX: px });
					}}
					onPointerLeave={() => setHover(null)}
				>
					<g transform={`translate(${marginLeft},${MARGIN.top})`}>
						{/* 横目盛線。基準線 (0) だけ少し濃くする。 */}
						{yTicks.map((tick) => (
							<g key={tick}>
								<line
									x1={0}
									x2={plotWidth}
									y1={yForValue(tick)}
									y2={yForValue(tick)}
									stroke={tick === 0 ? "var(--fg-3)" : "var(--line-1)"}
									strokeWidth={1}
								/>
								<text
									x={-8}
									y={yForValue(tick)}
									textAnchor="end"
									dominantBaseline="middle"
									style={{
										fontSize: "var(--text-section)",
										fill: "var(--fg-3)",
										fontVariantNumeric: "tabular-nums",
									}}
								>
									{compactFormat.format(tick)}
								</text>
							</g>
						))}

						{/* ホバー中のスロットの背景帯 (縦棒) / 基準線 (面)。 */}
						{hover !== null && hoveredBucket !== undefined && data.kind === "column" && (
							<rect
								x={xForIndex(hover.index) - plotWidth / buckets.length / 2}
								y={0}
								width={plotWidth / buckets.length}
								height={PLOT_HEIGHT}
								fill="var(--surface-2)"
								opacity={0.7}
							/>
						)}

						{data.kind === "column" ? (
							<Columns
								buckets={buckets}
								bands={bands}
								xForIndex={xForIndex}
								yForValue={yForValue}
								plotWidth={plotWidth}
							/>
						) : (
							<Areas buckets={buckets} bands={bands} xForIndex={xForIndex} yForValue={yForValue} />
						)}

						{hover !== null && hoveredBucket !== undefined && data.kind === "area" && (
							<line
								x1={xForIndex(hover.index)}
								x2={xForIndex(hover.index)}
								y1={0}
								y2={PLOT_HEIGHT}
								stroke="var(--fg-3)"
								strokeWidth={1}
							/>
						)}

						{/* 日付目盛。 */}
						{xTicks.map((tick) => (
							<text
								key={tick.index}
								x={xForIndex(tick.index)}
								y={PLOT_HEIGHT + 15}
								textAnchor={xTickAnchor(tick.index, buckets.length)}
								style={{ fontSize: "var(--text-section)", fill: "var(--fg-3)" }}
							>
								{tick.label}
							</text>
						))}
					</g>
				</svg>
			)}
			{hover !== null && hoveredBucket !== undefined && (
				<UsageTooltip
					data={data}
					bucket={hoveredBucket}
					x={marginLeft + xForIndex(hover.index)}
					containerWidth={width}
				/>
			)}
		</div>
	);
}

/** 積み上げ縦棒。下から bands の順に積み、最上段だけ上角を丸める。 */
function Columns({
	buckets,
	bands,
	xForIndex,
	yForValue,
	plotWidth,
}: {
	buckets: readonly UsageChartBucket[];
	bands: readonly Band[];
	xForIndex: (index: number) => number;
	yForValue: (value: number) => number;
	plotWidth: number;
}) {
	const slotWidth = plotWidth / buckets.length;
	const columnWidth = Math.min(MAX_COLUMN_WIDTH, slotWidth * 0.66);

	return (
		<g>
			{buckets.map((bucket, index) => {
				const segments = bands
					.map((band) => ({ ...band, credits: band.credits(bucket) }))
					.filter((s) => s.credits > 0);
				if (segments.length === 0) return null;
				const x = xForIndex(index) - columnWidth / 2;
				let cumulative = 0;
				// 次のセグメントの下端。最小の高さまで広げた分は上のセグメントを縮めて吸収し、
				// 棒の上端を合計の位置に保つ。
				let base = yForValue(0);
				return (
					<g key={bucket.date}>
						{segments.map((segment, segmentIndex) => {
							const yBottom = yForValue(cumulative);
							cumulative += segment.credits;
							const yTop = yForValue(cumulative);
							const isTop = segmentIndex === segments.length - 1;
							// 上のセグメントとの間に地色の隙間を空ける (潰れるほど薄い場合は省く)。
							const gap = !isTop && yBottom - yTop > SEGMENT_GAP * 2 ? SEGMENT_GAP : 0;
							const minHeight = isTop
								? MIN_TOP_SEGMENT_HEIGHT
								: segment.focused
									? MIN_FOCUSED_SEGMENT_HEIGHT
									: 0;
							const height = Math.max(base - yTop - gap, minHeight);
							const y = base - height;
							base = y - gap;
							if (isTop) {
								return (
									<path
										key={segment.key}
										d={topRoundedRectPath(x, y, columnWidth, height, 4)}
										fill={segment.fill}
									/>
								);
							}
							return (
								<rect
									key={segment.key}
									x={x}
									y={y}
									width={columnWidth}
									height={height}
									fill={segment.fill}
								/>
							);
						})}
					</g>
				);
			})}
		</g>
	);
}

/** 積み上げ面グラフ。帯を下から順に塗り、帯の境界は地色の細線で分ける。 */
function Areas({
	buckets,
	bands,
	xForIndex,
	yForValue,
}: {
	buckets: readonly UsageChartBucket[];
	bands: readonly Band[];
	xForIndex: (index: number) => number;
	yForValue: (value: number) => number;
}) {
	// cumulative[k][i] = バケット i で帯 0..k-1 を積んだ値。
	const cumulative: number[][] = [buckets.map(() => 0)];
	for (const band of bands) {
		const previous = cumulative.at(-1)!;
		cumulative.push(buckets.map((b, i) => previous[i] + band.credits(b)));
	}

	const point = (i: number, v: number): string => `${xForIndex(i)},${yForValue(v)}`;
	const linePoints = (values: number[]): string => values.map((v, i) => point(i, v)).join(" L");
	// 帯の輪郭 = 上端の折れ線 + 下端の折れ線を逆順に戻る閉路。
	const reversedLinePoints = (values: number[]): string =>
		values
			.map((v, i) => point(i, v))
			.toReversed()
			.join(" L");
	// 帯の上端の境界線。厚みの無い区間で引くと 0 の基準線を地色で消すので、
	// 両端とも厚みの無い区間は飛ばす。
	const boundaryPath = (upper: number[], lower: number[]): string =>
		upper
			.slice(1)
			.flatMap((v, i) =>
				upper[i] > lower[i] || v > lower[i + 1]
					? [`M${point(i, upper[i])} L${point(i + 1, v)}`]
					: [],
			)
			.join(" ");

	return (
		<g>
			{bands.map((band, k) => {
				const upper = cumulative[k + 1];
				const lower = cumulative[k];
				const path = `M${linePoints(upper)} L${reversedLinePoints(lower)} Z`;
				return (
					<g key={band.key}>
						<path d={path} fill={band.fill} />
						{/* 最上段の上端は背景に接するので境界線は不要。 */}
						{k < bands.length - 1 && (
							<path
								d={boundaryPath(upper, lower)}
								fill="none"
								stroke="var(--surface-1)"
								strokeWidth={1.5}
								strokeLinecap="round"
							/>
						)}
					</g>
				);
			})}
		</g>
	);
}

/** ホバー中のバケットの内訳ツールチップ。 */
function UsageTooltip({
	data,
	bucket,
	x,
	containerWidth,
}: {
	data: UsageChartData;
	bucket: UsageChartBucket;
	x: number;
	containerWidth: number;
}) {
	// プロット中央より右では左側へ出し、はみ出しを避ける。
	const flip = x > containerWidth / 2;
	return (
		<div
			data-testid="billing-usage-chart-tooltip"
			style={{
				position: "absolute",
				top: 0,
				left: flip ? undefined : Math.min(x + 12, containerWidth - 12),
				right: flip ? containerWidth - x + 12 : undefined,
				zIndex: 10,
				minWidth: 180,
				padding: "8px 10px",
				background: "var(--surface-1)",
				boxShadow: "var(--shadow-ring)",
				borderRadius: "var(--radius-3)",
				pointerEvents: "none",
			}}
		>
			<div
				style={{
					fontSize: "var(--text-caption)",
					fontWeight: 600,
					color: "var(--fg-2)",
					marginBottom: 6,
				}}
			>
				{bucketDateLabel(bucket)}
			</div>
			<div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
				{/* グラフの段と上下を揃え、上に積んだカテゴリから並べる (一覧とは逆順)。 */}
				{data.categories.toReversed().map(({ category }) => (
					<div key={category.id} style={{ display: "flex", alignItems: "center", gap: 6 }}>
						<span
							style={{
								width: 8,
								height: 8,
								flexShrink: 0,
								borderRadius: "var(--radius-1)",
								background: category.color,
							}}
						/>
						<span
							style={{ fontSize: "var(--text-caption)", color: "var(--fg-2)", marginRight: "auto" }}
						>
							{category.label}
						</span>
						<span
							style={{
								fontSize: "var(--text-caption)",
								fontWeight: 600,
								color: "var(--fg-1)",
								fontVariantNumeric: "tabular-nums",
							}}
						>
							{formatCredits(bucket.byCategory[category.id] ?? 0)}
						</span>
					</div>
				))}
			</div>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					gap: 6,
					marginTop: 6,
					paddingTop: 6,
					borderTop: "var(--pane-header-border)",
				}}
			>
				<span
					style={{
						fontSize: "var(--text-caption)",
						fontWeight: 600,
						color: "var(--fg-2)",
						marginRight: "auto",
					}}
				>
					合計
				</span>
				<span
					style={{
						fontSize: "var(--text-caption)",
						fontWeight: 600,
						color: "var(--fg-1)",
						fontVariantNumeric: "tabular-nums",
					}}
				>
					{formatCredits(bucket.total)}
				</span>
			</div>
		</div>
	);
}

/** バケットの日付表示。週バケットは期間で示す (末尾は部分週のことがある)。 */
function bucketDateLabel(bucket: UsageChartBucket): string {
	const start = parseDateKey(bucket.date);
	if (bucket.days <= 1) return formatJstDate(start, true);
	const end = new Date(start);
	end.setUTCDate(end.getUTCDate() + bucket.days - 1);
	return `${formatJstDate(start, true)} 〜 ${formatJstDate(end, start.getUTCFullYear() !== end.getUTCFullYear())}`;
}

/** yyyy-MM-dd (JST 暦日) を UTC 深夜の Date として読む (暦計算専用)。 */
function parseDateKey(dateKey: string): Date {
	return new Date(`${dateKey}T00:00:00Z`);
}

function formatJstDate(date: Date, withYear: boolean): string {
	const md = `${date.getUTCMonth() + 1}/${date.getUTCDate()}`;
	return withYear ? `${date.getUTCFullYear()}/${md}` : md;
}

/** 上角だけ丸めた矩形のパス。丸めは高さ・幅に収まる範囲へ切り詰める。 */
function topRoundedRectPath(x: number, y: number, w: number, h: number, r: number): string {
	const rr = Math.min(r, h, w / 2);
	return (
		`M${x},${y + h} L${x},${y + rr} Q${x},${y} ${x + rr},${y} ` +
		`L${x + w - rr},${y} Q${x + w},${y} ${x + w},${y + rr} L${x + w},${y + h} Z`
	);
}

/**
 * 最大値を 1/2/5 × 10^n の切りのよい値へ切り上げる。クレジットはクレジット単位で
 * 表示するため、1 未満へは切り下げない。
 */
function niceMax(value: number): number {
	if (!(value > 1)) return 1;
	const exponent = Math.floor(Math.log10(value));
	const base = 10 ** exponent;
	for (const step of [1, 2, 5, 10]) {
		if (value <= step * base) return step * base;
	}
	return 10 * base;
}

/**
 * 0 を含む横目盛。目盛のラベルもクレジット単位で表示するため、刻みが整数になる分割数を
 * 選ぶ (yMax が小さいときは本数が減る)。yMax は niceMax 済みなので必ずどれかで割り切れる。
 */
function buildYTicks(yMax: number): number[] {
	const divisions = [4, 5, 2, 1].find((d) => Number.isInteger(yMax / d)) ?? 1;
	return Array.from({ length: divisions + 1 }, (_, i) => (yMax / divisions) * i);
}

/**
 * 日付目盛の文字の寄せ。両端の目盛はプロットの端に近く (面グラフは端ちょうど、縦棒も
 * スロットの半分しか離れない)、中央寄せでは年付きの日付が SVG の外へはみ出して切れるので、
 * 内側へ寄せる。
 */
function xTickAnchor(index: number, bucketCount: number): "start" | "middle" | "end" {
	if (bucketCount === 1) return "middle";
	if (index === 0) return "start";
	if (index === bucketCount - 1) return "end";
	return "middle";
}

/** 日付目盛。幅に収まる本数を等間隔に選び、年が変わる目盛には年を付ける。 */
function buildXTicks(
	buckets: readonly UsageChartBucket[],
	plotWidth: number,
): { index: number; label: string }[] {
	if (buckets.length === 0 || plotWidth <= 0) return [];
	const tickCount = Math.max(Math.min(Math.floor(plotWidth / 72), buckets.length), 1);
	const ticks: { index: number; label: string }[] = [];
	let previousYear: number | null = null;
	for (let i = 0; i < tickCount; i++) {
		const index = tickCount === 1 ? 0 : Math.round((i / (tickCount - 1)) * (buckets.length - 1));
		if (ticks.some((t) => t.index === index)) continue;
		const date = parseDateKey(buckets[index].date);
		const year = date.getUTCFullYear();
		ticks.push({ index, label: formatJstDate(date, previousYear !== year) });
		previousYear = year;
	}
	return ticks;
}
